import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Réduit un contenu de largeur fixe pour qu'il tienne dans la place offerte.
 *
 * Le bulletin de versement suisse a des dimensions légales : 210 mm de large,
 * une grille en deux colonnes, des corps de texte imposés. On ne peut pas le
 * laisser se réagencer sur un écran de téléphone — une banque refuse un
 * bulletin dont le lecteur ne retrouve pas les repères.
 *
 * Il est donc dessiné à sa largeur réelle et l'ensemble est mis à l'échelle,
 * proportions intactes. À l'impression, la CSS annule la transformation :
 * c'est l'aperçu qui s'adapte, jamais le document.
 *
 * @param {object} props propriétés du composant
 * @param {number} props.baseWidth largeur à laquelle le contenu est dessiné
 * @param {import('react').ReactNode} props.children contenu à réduire
 * @returns {import('react').ReactElement} le contenu, réduit si besoin
 */
export function ScaleFit({ baseWidth, children }) {
  const cadre = useRef(null);
  const contenu = useRef(null);
  const [etat, setEtat] = useState({ echelle: 1, hauteur: undefined });

  // Mesure avant la peinture : un premier rendu a taille reelle ferait
  // deborder le document le temps d'une image.
  useLayoutEffect(() => {
    const mesure = () => {
      if (!cadre.current || !contenu.current) {
        return;
      }
      const place = cadre.current.clientWidth || baseWidth;
      // Jamais d'agrandissement : le bulletin ne gagne rien a etre etire, et
      // un QR grossi devient flou.
      const echelle = Math.min(1, place / baseWidth);
      setEtat({ echelle, hauteur: Math.ceil(contenu.current.offsetHeight * echelle) });
    };

    mesure();
    // Une seconde mesure differee : les polices et le QR arrivent apres le
    // premier rendu et changent la hauteur.
    const differee = setTimeout(mesure, 60);

    let observateur = null;
    if (typeof ResizeObserver !== 'undefined') {
      observateur = new ResizeObserver(mesure);
      observateur.observe(cadre.current);
      observateur.observe(contenu.current);
    }
    window.addEventListener('resize', mesure);

    return () => {
      clearTimeout(differee);
      if (observateur) {
        observateur.disconnect();
      }
      window.removeEventListener('resize', mesure);
    };
  }, [baseWidth]);

  return (
    <div
      ref={cadre}
      className="qr-scale-wrap"
      style={{ width: '100%', height: etat.hauteur, overflow: 'hidden' }}
    >
      <div
        ref={contenu}
        className="qr-scale-inner"
        style={{
          width: baseWidth,
          // Contient la marge haute du premier enfant : sans cela elle
          // traverse ce bloc, decale le contenu et echappe a `offsetHeight`,
          // et le bas du bulletin etait rogne d'autant.
          display: 'flow-root',
          transformOrigin: 'top left',
          transform: 'scale(' + etat.echelle + ')',
        }}
      >
        {children}
      </div>
    </div>
  );
}
